/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm, SortDirection, Sorting, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/api/api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { validate } from 'app/shared/elements/ess-rule';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { CaseService } from 'app/cm/services/case-service';
import * as CM from 'app/cm/models/essette-api';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { SaveHouseholdCaseService } from 'app/cm/components/household/services/save-household-case-service';
import { HouseholdCasePageContext } from 'app/cm/lib/household-case-page-context';

@autoinject()
export class HouseholdCaseServicesGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public householdCaseId: string | null = null;

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = false;

    protected services: Array<CM.CaseService> = [];

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 20;
    protected validate = validate;
    protected isValid: boolean = false;
    protected canAddService: boolean = true;

    protected showMemberSummaryLink: boolean = false;
    protected showCaseSummaryLink: boolean = false;
    protected hasCaseRoute = false;

    protected openMemberSummary = openMemberSummary;
    protected openCaseSummary = openCaseSummary;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: EssModalCustomElement;
    protected $form!: IForm;
    protected $serviceForm!: Element;
    protected $saveCaseService!: SaveHouseholdCaseService;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly caseService: CaseService,
        private readonly context: HouseholdCasePageContext,
        ...rest: any[]) {

        super(...rest);
        this.checkPermissions();
    }

    protected bind(): void {
        this.checkPermissions();
        this.reload();
    }


    ////****************** Events ***************************************************////
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected formSuccess({ detail }: FormSuccessCustomEvent): void {
        this.$modal.close();

        if(detail.form && detail.form.reset) {
            detail.form.reset();
        }

        this.reload();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadServices();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
        this.canAddService = this.accountContext.hasPermission('CM.CaseService.Insert.AddService');
    }

    protected isLatestHistory(historyEntry: CM.CaseServiceHistory) {
        return historyEntry.metadata?.ended?.timestamp === null;
    }

    protected addService() {
        if(!this.householdCaseId) {
            throw new Error('Missing required property: [householdCaseId]');
        }

        this.$modal.open();
    }

    protected modalClose(): void {
        if(this.$form.reset) {
            this.$form.reset();
        }
        this.$modal.close();
    }

    protected getTaskLabel(task?: CM.Task): string {
        if(!task) return '';
        return task.attachedTo?.type?.code === 'CM.CarePlanGoal' ? 'cm:carePlanIntervention.$' : 'core:task.$';
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadServices(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
        };

        const includes: string[] = [
            'task',
            'service',
            'placeOfService',
            'quantity.unit',
            'metadata.created.byUser',
            'metadata.modified.byUser',
            'modifier',
            'case',
            'member'
        ];

        try {
            this.state.beginLoad();
            const response = await this.caseService.loadHouseholdCaseServices(this.householdCaseId, options, includes);
            this.services = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
